import { useEffect, useState } from "react";

interface Props {
  id: string;
  /** YYYY-MM-DD, or undefined while empty or unparseable. */
  value: string | undefined;
  onChange: (next: string | undefined) => void;
  "data-testid"?: string;
}

/** "14.05.2026" for a YYYY-MM-DD date. */
function formatDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

/**
 * "14.5.2026", "14.05.2026" or "14/5/2026" → "2026-05-14"; null unless it
 * names a real calendar date.
 */
function parseDate(raw: string): string | null {
  const match = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(raw.trim());
  if (!match) return null;
  const [day, month, year] = [match[1], match[2], match[3]].map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * A dd.mm.åååå date field. Deliberately NOT `<input type="date">`, for the
 * reason TimeField gives: the native control takes its format from the
 * browser's UI language, so an English-UI browser shows mm/dd/yyyy whatever
 * the page says.
 *
 * Commits the moment the text names a real date; text that never does is
 * discarded on blur, leaving the last good value.
 */
export function DateField({
  id,
  value,
  onChange,
  "data-testid": testId,
}: Props) {
  const [draft, setDraft] = useState(value ? formatDate(value) : "");
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(value ? formatDate(value) : "");
  }, [value, focused]);

  return (
    <input
      id={id}
      type="text"
      className="input date-input"
      inputMode="numeric"
      autoComplete="off"
      maxLength={10}
      placeholder="dd.mm.åååå"
      value={draft}
      onFocus={() => setFocused(true)}
      onChange={(e) => {
        setDraft(e.target.value);
        const parsed = parseDate(e.target.value);
        if (parsed !== null) onChange(parsed);
      }}
      onBlur={() => {
        setFocused(false);
        setDraft(value ? formatDate(value) : "");
      }}
      data-testid={testId}
    />
  );
}
